import * as CL from '@design-system/component-library';
import { generatePath } from 'react-router';
import { pushCtaClickEventToDataLayer } from '../../common/analytics';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables.js';
import {
  dueDateMsg,
  invoiceMsg,
  noResultsFoundMsg,
  paidMsg,
  payerDetailsMsg,
  pukCodeMsg,
  t,
  viewAllMsg,
} from '../../common/i18n';
import { getCompanyName } from '../../common/utils/accountUtils';
import { getDeliveryMethod } from '../../common/utils/billingAccountUtils';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils';
import { formatBalance, formatSum } from '../../common/utils/priceUtils';
import { truncateText } from '../../common/utils/stringUtils';
import { useAuth } from '../../public/site/AuthProvider';
import { getStatusColumnInvoiceState } from '../InvoiceDetails/InvoiceDetails';
import { Status } from '../Status/Status';
import { Modal } from './Modal.js';
import type { BillingAccountHeader } from '../../generated/api/billingAccountHeader';
import type { InvoiceHeader } from '../../generated/api/invoiceHeader';
import type { SubscriptionHeader } from '../../generated/api/subscriptionHeader';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { JSX } from 'react';

import './LinkListModal.scss';

type ModalType = 'invoices' | 'puk' | 'billingAccounts' | 'connectionProblems';

type ListData = InvoiceHeader[] | SubscriptionHeader[] | BillingAccountHeader[] | undefined;

interface LinkItemData {
  id: string;
  title: JSX.Element | string;
  subtitle?: JSX.Element | string;
  status?: JSX.Element | string;
  rightSection?: JSX.Element | string;
  path: string;
}

interface LinkListModalProps {
  heading: string;
  data: ListData;
  modalType: ModalType;
  icon: string;
  showAllLinkRoute?: string;
  onClose: () => void;
}

const mapDataToLinkItems = (
  items: ListData,
  modalType: ModalType,
  authenticatedUser?: AuthenticatedUser
): LinkItemData[] => {
  switch (modalType) {
    case 'puk':
    case 'connectionProblems':
      return (items as SubscriptionHeader[]).map(item => ({
        id: item.subscriptionId,
        title: (
          <div className="of-link-list-modal__link__title">
            <span className={modalType === 'connectionProblems' ? dsClass.TEXT_BOLD : ''}>
              {truncateText(item.subscriptionContactName ?? item.subscriptionPurposeOfUse, 50)}
            </span>
            <span className={`of-link-list-modal__link__separator ${dsClass.PADDING_HORIZONTAL_1}`}>|</span>
            {formatPhoneNumber(item.subscriptionUserFriendlyId ?? '')}
          </div>
        ),
        subtitle: (
          <>
            {item.subscriptionName}
            <span className={`of-link-list-modal__link__separator ${dsClass.PADDING_HORIZONTAL_1}`}>|</span>
            <div className={modalType === 'puk' ? dsClass.TEXT_BOLD : ''}>
              {t.K5TL(pukCodeMsg)}: {item.puk1 ?? '–'}
            </div>
          </>
        ),
        rightSection: getCompanyName(authenticatedUser, item.accountMasterId),
        path: `${generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: item.subscriptionDisplayId })}?mdmId=${item.accountMasterId}`,
      }));

    case 'invoices':
      return (items as InvoiceHeader[]).map(item => ({
        id: item.invoiceDisplayId,
        title: (
          <div className="of-link-list-modal__link__title">
            <strong>
              {t.JF65(invoiceMsg)}: {item.invoiceDisplayId}
            </strong>
            <span className={`of-link-list-modal__link__separator ${dsClass.PADDING_HORIZONTAL_1}`}>|</span>
            {t.LA93(dueDateMsg)} {formatTimestampToDDMMYYYY(item.due)}
          </div>
        ),
        subtitle: (
          <>
            <Status
              textAfterBadge={getStatusColumnInvoiceState(item.balance ?? 0, item.due).text}
              color={getStatusColumnInvoiceState(item.balance ?? 0, item.due).color}
              smallBadge
            />
            <span className="of-link-list-modal__link__separator">|</span>
            <div className="of-link-list-modal__link__subtitle__extra">
              {formatSum(item.sum)}, {t.RJ27(paidMsg)} {formatBalance(item.sum, item.balance ?? 0)}
            </div>
          </>
        ),
        rightSection: item.payerName,
        path: `${generatePath(paths.INVOICE, { invoiceId: item.invoiceDisplayId })}?mdmId=${item.accountMasterId}`,
      }));

    case 'billingAccounts':
      return (items as BillingAccountHeader[]).map(item => ({
        id: item.billingAccountDisplayId ?? 'unknown',
        title: (
          <div className="of-link-list-modal__link__title">
            <strong>{item.billingAccountDisplayId}</strong>
            <span className={`of-link-list-modal__link__separator ${dsClass.PADDING_HORIZONTAL_1}`}>|</span>
            {item.payerName}
          </div>
        ),
        subtitle: (
          <>
            {getDeliveryMethod(item.deliveryMethod ?? '')}
            {item.payerNameExtension && (
              <>
                <span className={`of-link-list-modal__link__separator ${dsClass.PADDING_HORIZONTAL_1}`}>|</span>
                <div>
                  {t.YLAI(payerDetailsMsg)}: {item.payerNameExtension}
                </div>
              </>
            )}
          </>
        ),
        rightSection: getCompanyName(authenticatedUser, item.accountMasterId),
        path: `${generatePath(paths.BILLING_ACCOUNT, { billingAccountId: item.billingAccountDisplayId ?? '' })}?mdmId=${item.accountMasterId}`,
      }));

    default:
      return [];
  }
};

export const LinkListModal = ({ heading, data, modalType, icon, showAllLinkRoute, onClose }: LinkListModalProps) => {
  const { authenticatedUser } = useAuth();
  const linkItems = data ? mapDataToLinkItems(data, modalType, authenticatedUser) : [];

  return (
    <Modal className="of-link-list-modal" closeable size="l" onClose={onClose}>
      <p
        className={`${dsClass.MARGIN_VERTICAL_2} ${dsClass.MARGIN_LEFT_3} ${dsClass.PADDING_TOP_2} ${dsClass.TEXT_XS} ${dsClass.TEXT_TRANSFORM_UPPERCASE}`}
      >
        {heading} ({data?.length ?? 0})
      </p>

      {linkItems.map(item => {
        const handleLinkClick = () => {
          pushCtaClickEventToDataLayer('NOE Favorites section', undefined, item.path);
        };

        return (
          <CL.LinkBlock
            className={`${dsClass.BORDER_STYLE_NONE} ${dsClass.MARGIN_0} ${dsClass.PADDING_3}`}
            key={item.id}
            href={item.path}
            arrow={false}
            icon={<CL.Icon icon={icon} />}
            onClick={handleLinkClick}
          >
            <div className="of-link-list-modal__link">
              <div className="of-link-list-modal__link__left">
                {item.title}
                {item.subtitle && <div className="of-link-list-modal__link__subtitle">{item.subtitle}</div>}
              </div>
              {item.rightSection && <div className="of-link-list-modal__link__right">{item.rightSection}</div>}
            </div>
          </CL.LinkBlock>
        );
      })}

      {!linkItems.length && (
        <div className="of-link-list-modal__empty">
          <div className="of-link-list-modal__empty__icon ea-picto--invoice"></div>
          {t.OQBT(noResultsFoundMsg)}
        </div>
      )}

      {showAllLinkRoute && (
        <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_FLEX_END} ${dsClass.MARGIN_RIGHT_3}`}>
          <CL.Link
            className={dsClass.COLOR_BRAND_BLUE}
            linkStyle="link-button"
            buttonColor="link"
            buttonSize="s"
            linkHref={showAllLinkRoute}
            onClick={() => pushCtaClickEventToDataLayer('NOE Favorites section', undefined, showAllLinkRoute)}
          >
            <div className={dsClass.DISPLAY_FLEX}>
              <span>{t.SF4C(viewAllMsg)}</span>
              <CL.Icon className={dsClass.MARGIN_LEFT_2} icon="arrow-right-alt" type="filled" />
            </div>
          </CL.Link>
        </div>
      )}
    </Modal>
  );
};
